import { useTranslation } from "react-i18next";

import { FileChooserDropzoneTrigger } from "simplydo/react/components/Choosers/FileChooser/FileChooserDropzoneTrigger";
import { allowedImageTypesList } from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { styled } from "simplydo/react/styled";
import { Button, Card, Icon, Segment } from "simplydo/react/ui";

import { ImageChooser } from "components/lib/Choosers";

import { FieldProps } from "./FieldProps";

const StyledImage = styled.img`
  max-width: 100%;
  display: block;
  object-fit: contain;
`;

type ImageComponentProps = { image: string; onRemove?: () => void };

const ImageComponent = ({ image, onRemove }: ImageComponentProps) => {
  const { t } = useTranslation();

  return (
    <Card.Group centered>
      <Card style={{ width: "80%" }}>
        <Card.Content style={{ color: "#808080" }}>
          <Icon name="images" />
          Uploaded image
        </Card.Content>
        <Card.Content textAlign="center">
          <Card.Header style={{ overflowWrap: "break-word" }}>
            <StyledImage
              alt={`${t("common:capitalise", { key: "generic.idea" })} field content`}
              src={image}
            />
          </Card.Header>
        </Card.Content>
        <Card.Content extra>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Button
              as="a"
              href={image}
              target="_blank"
              rel="noopener noreferrer"
              download={image}
              fluid
              basic
              icon="download"
              content={t("generic.download")}
              style={{ marginLeft: 0, marginRight: 0 }}
              size="small"
            />
            {onRemove ? (
              <Button
                basic
                size="small"
                icon="trash"
                onClick={onRemove}
                style={{ marginLeft: 0, marginRight: 0 }}
              />
            ) : null}
          </div>
        </Card.Content>
      </Card>
    </Card.Group>
  );
};

const Image = ({
  field,
  ideaId,
  fieldValue = "",
  updateIdea,
  isEditing,
}: FieldProps) => {
  const imageSrc = typeof fieldValue === "string" ? fieldValue : "";
  if (!isEditing) {
    if (imageSrc) {
      return <ImageComponent image={imageSrc} />;
    }
    return null;
  }

  if (imageSrc) {
    return (
      <Segment placeholder>
        <ImageComponent
          image={imageSrc}
          onRemove={() => updateIdea(null, ["templated", field.id])}
        />
      </Segment>
    );
  }

  return (
    <ImageChooser
      trigger={
        <FileChooserDropzoneTrigger
          forType="idea"
          accept={allowedImageTypesList.join(",")}
          forId={ideaId}
          onComplete={(_value, _name, imageSrc) =>
            updateIdea(imageSrc, ["templated", field.id])
          }
          dropzoneProps={{
            title: "Upload an image",
            DropZoneComponent: Segment,
            defaultIcon: "image",
          }}
        />
      }
      forType="idea"
      forId={ideaId}
      onComplete={(_value, _name, imageSrc) =>
        updateIdea(imageSrc, ["templated", field.id])
      }
    />
  );
};

export default Image;
